<script setup lang="ts">
import { ArrowRight, Check, Menu, Mic, Play, Video, X } from 'lucide-vue-next'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import logoSrc from '../../../base/src/assets/logo.trans.svg'
import { useI18n } from '../../../base/src/composables/useI18n'
import { useSiteStore } from '../../../base/src/stores/site'
import { useAuthStore } from '../../../base/src/stores/auth'
import heroAvatar from '../assets/intro/hero-avatar.webp'
import voiceImage from '../assets/intro/section-voice.webp'
import videoImage from '../assets/intro/section-video.webp'
import en from '../locales/en'
import zh from '../locales/zh'
import IntroFeatureGrid from './intro/IntroFeatureGrid.vue'
import IntroFooter from './intro/IntroFooter.vue'
import IntroShowcase from './intro/IntroShowcase.vue'
import IntroWays from './intro/IntroWays.vue'

const router = useRouter()
const { t } = useI18n({ zh, en })
const auth = useAuthStore()
const siteStore = useSiteStore()
const isAuthed = computed(() => auth.isAuthenticated)

// Header starts transparent over the hero gradient and turns into a glass bar on scroll
const scrolled = ref(false)
const mobileOpen = ref(false)
const onScroll = () => {
  scrolled.value = window.scrollY > 8
}
onMounted(() => {
  onScroll()
  window.addEventListener('scroll', onScroll, { passive: true })
})
onBeforeUnmount(() => window.removeEventListener('scroll', onScroll))

const navLinks = computed(() => [
  { id: 'features', label: t('muse.intro.nav.features') },
  { id: 'voice', label: t('muse.intro.nav.voice') },
  { id: 'video', label: t('muse.intro.nav.video') },
  { id: 'ways', label: t('muse.intro.nav.ways') },
])

const voiceBullets = computed(() => [
  t('muse.intro.voice.bullet1'),
  t('muse.intro.voice.bullet2'),
  t('muse.intro.voice.bullet3'),
])

const videoBullets = computed(() => [
  t('muse.intro.video.bullet1'),
  t('muse.intro.video.bullet2'),
  t('muse.intro.video.bullet3'),
])

const stats = computed(() => [
  { value: t('muse.intro.stats.cloneValue'), label: t('muse.intro.stats.cloneLabel') },
  { value: t('muse.intro.stats.qualityValue'), label: t('muse.intro.stats.qualityLabel') },
  { value: t('muse.intro.stats.langValue'), label: t('muse.intro.stats.langLabel') },
])

const waveform = [8, 14, 20, 12, 24, 16, 10, 22, 18, 26, 14, 9, 19, 13, 23, 11, 17, 21, 8, 15, 22, 12, 18, 10]

function go(path: string) {
  mobileOpen.value = false
  router.push(path)
}

function scrollTo(id: string) {
  mobileOpen.value = false
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function primaryAction() {
  go(isAuthed.value ? '/user/muse/dashboard' : '/register')
}

function secondaryAction() {
  scrollTo('features')
}

function openVoice() {
  go(isAuthed.value ? '/user/muse/voice' : '/register')
}

function openVideo() {
  go(isAuthed.value ? '/user/muse/avatarGenerate' : '/register')
}
</script>

<template>
  <div data-testid="muse-intro" class="bg-surface text-gray-900 dark:bg-surface dark:text-gray-100">
    <!-- Top bar: transparent over the hero, glass after scrolling -->
    <header
      class="fixed inset-x-0 top-0 z-30 border-b transition-[background-color,border-color,box-shadow] duration-300"
      :class="
        scrolled
          ? 'border-gray-100/80 bg-surface/80 shadow-[0_6px_24px_-16px_rgba(15,23,42,0.35)] backdrop-blur-xl dark:border-gray-800/80 dark:bg-surface/85'
          : 'border-transparent bg-transparent'
      "
    >
      <div class="mx-auto grid h-16 max-w-6xl grid-cols-[1fr_auto] items-center px-5 sm:h-20 sm:px-8 md:grid-cols-[1fr_auto_1fr]">
        <div class="flex cursor-pointer items-center gap-2.5 justify-self-start" @click="go('/')">
          <img :src="logoSrc" class="h-8 w-8 object-contain" alt="" />
          <div class="text-base font-bold tracking-tight sm:text-lg">{{ siteStore.displayName }}</div>
        </div>

        <nav class="hidden items-center gap-5 justify-self-center text-sm text-gray-600 md:flex lg:gap-7 dark:text-gray-300">
          <a
            v-for="link in navLinks"
            :key="link.id"
            :href="`#${link.id}`"
            class="rounded-md text-gray-600 no-underline transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/50 focus-visible:ring-offset-2 dark:text-gray-300"
            @click.prevent="scrollTo(link.id)"
          >
            {{ link.label }}
          </a>
        </nav>

        <div class="flex items-center gap-1.5 justify-self-end sm:gap-3">
          <a-button v-if="!isAuthed" type="text" class="hidden !px-2 sm:inline-flex sm:!px-3" @click="go('/login')">
            {{ t('muse.intro.login') }}
          </a-button>
          <a-button type="primary" class="!h-9 !rounded-full !px-4 sm:!h-10 sm:!px-5" @click="primaryAction">
            {{ isAuthed ? t('muse.intro.hero.ctaApp') : t('muse.intro.start') }}
          </a-button>
          <div
            class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-primary-50 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/50 md:hidden dark:text-gray-300 dark:hover:bg-gray-800"
            role="button"
            tabindex="0"
            :aria-label="t('muse.intro.nav.menu')"
            :aria-expanded="mobileOpen"
            @click="mobileOpen = !mobileOpen"
            @keydown.enter="mobileOpen = !mobileOpen"
          >
            <X v-if="mobileOpen" class="h-5 w-5" aria-hidden="true" />
            <Menu v-else class="h-5 w-5" aria-hidden="true" />
          </div>
        </div>
      </div>

      <div
        v-show="mobileOpen"
        class="absolute inset-x-0 top-full border-b border-gray-100/80 bg-surface/95 px-4 py-2 shadow-lg backdrop-blur-xl md:hidden dark:border-gray-800/80 dark:bg-surface/95"
      >
        <a
          v-for="link in navLinks"
          :key="link.id"
          :href="`#${link.id}`"
          class="block rounded-lg px-3 py-2.5 text-sm text-gray-600 no-underline transition-colors hover:bg-primary-50 hover:text-primary dark:text-gray-300 dark:hover:bg-gray-800"
          @click.prevent="scrollTo(link.id)"
        >
          {{ link.label }}
        </a>
        <div class="my-2 h-px bg-gray-100 dark:bg-gray-800"></div>
        <a
          v-if="!isAuthed"
          href="/login"
          class="block rounded-lg px-3 py-2.5 text-sm font-medium text-primary no-underline transition-colors hover:bg-primary-50 dark:hover:bg-gray-800"
          @click.prevent="go('/login')"
        >
          {{ t('muse.intro.login') }}
        </a>
        <a
          v-else
          href="/user/muse/dashboard"
          class="block rounded-lg px-3 py-2.5 text-sm font-medium text-primary no-underline transition-colors hover:bg-primary-50 dark:hover:bg-gray-800"
          @click.prevent="primaryAction"
        >
          {{ t('muse.intro.hero.ctaApp') }}
        </a>
      </div>
    </header>

    <!-- Hero -->
    <section class="relative overflow-hidden">
      <div class="pointer-events-none absolute -top-32 -left-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl dark:bg-primary/10"></div>
      <div class="pointer-events-none absolute top-10 right-[-6rem] h-96 w-96 rounded-full bg-accent/15 blur-3xl dark:bg-accent/10"></div>
      <div
        class="pointer-events-none absolute inset-0 bg-[radial-gradient(circle,rgba(148,163,184,0.22)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:linear-gradient(to_bottom,white,transparent_70%)]"
      ></div>

      <div class="relative mx-auto max-w-6xl px-5 pb-16 pt-28 text-center sm:px-8 sm:pb-20 sm:pt-36">
        <div
          class="inline-flex items-center gap-2 rounded-full border border-primary-100 bg-primary-50/80 px-3.5 py-1.5 text-[11px] font-medium text-primary sm:text-xs dark:border-primary-800/60 dark:bg-primary-900/30"
        >
          <span class="h-1.5 w-1.5 rounded-full bg-primary"></span>
          {{ t('muse.intro.badge') }}
        </div>

        <div class="mx-auto mt-5 max-w-4xl text-[30px] font-black leading-[1.18] tracking-tight sm:text-5xl lg:text-[54px]">
          <span class="inline-block whitespace-nowrap">{{ t('muse.intro.hero.titleLead') }}</span>
          <span class="mx-0.5 inline-block sm:mx-1"></span>
          <span class="inline-block whitespace-nowrap bg-gradient-to-r from-primary-700 via-primary-600 to-primary-500 bg-clip-text text-transparent">
            {{ t('muse.intro.hero.titleAccent') }}
          </span>
        </div>

        <div class="mx-auto mt-4 max-w-2xl text-pretty text-sm leading-relaxed text-gray-500 sm:mt-5 sm:text-base lg:text-lg dark:text-gray-400">
          {{ t('muse.intro.hero.subtitle', { name: siteStore.displayName }) }}
        </div>

        <div class="mt-7 flex flex-wrap items-center justify-center gap-3 sm:mt-8">
          <a-button type="primary" size="large" class="!h-12 !rounded-full !px-7 !font-medium" @click="primaryAction">
            <div class="inline-flex items-center gap-1.5">
              {{ isAuthed ? t('muse.intro.hero.ctaApp') : t('muse.intro.hero.ctaPublic') }}
              <ArrowRight class="h-4 w-4" aria-hidden="true" />
            </div>
          </a-button>
          <a-button size="large" class="!h-12 !rounded-full !px-6" @click="secondaryAction">
            {{ t('muse.intro.hero.secondaryPublic') }}
          </a-button>
        </div>

        <div class="mt-4 text-xs text-gray-400 dark:text-gray-500">{{ t('muse.intro.hero.note') }}</div>

        <!-- Product preview -->
        <div class="relative mx-auto mt-10 max-w-4xl sm:mt-14">
          <div class="absolute -inset-6 rounded-[40px] bg-gradient-to-r from-primary/25 via-accent/20 to-primary/20 opacity-60 blur-3xl"></div>
          <div
            class="relative overflow-hidden rounded-[26px] border border-white/70 bg-white/70 shadow-[0_24px_80px_rgba(15,23,42,0.14)] backdrop-blur sm:rounded-[34px] dark:border-white/10 dark:bg-panel/70"
          >
            <img
              :src="heroAvatar"
              :alt="t('muse.intro.hero.imageAlt')"
              class="h-[300px] w-full object-cover object-[center_18%] sm:h-[420px] lg:h-[500px]"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-gray-950/35 via-transparent to-transparent"></div>

            <div
              class="absolute left-3 top-3 flex items-center gap-2 rounded-full bg-white/85 px-3 py-2 text-[11px] font-medium text-gray-700 shadow-lg backdrop-blur sm:left-6 sm:top-6 sm:text-xs dark:bg-gray-900/75 dark:text-gray-200"
            >
              <Mic class="h-4 w-4 text-primary" aria-hidden="true" />
              {{ t('muse.intro.hero.chipVoice') }}
            </div>
            <div
              class="absolute right-3 top-3 hidden items-center gap-2 rounded-full bg-white/85 px-3 py-2 text-[11px] font-medium text-gray-700 shadow-lg backdrop-blur sm:flex sm:right-6 sm:top-6 sm:text-xs dark:bg-gray-900/75 dark:text-gray-200"
            >
              <Video class="h-4 w-4 text-accent" aria-hidden="true" />
              {{ t('muse.intro.hero.chipVideo') }}
            </div>
            <div
              class="absolute right-3 top-16 hidden items-center gap-2 rounded-full bg-white/85 px-3 py-2 text-[11px] font-medium text-gray-700 shadow-lg backdrop-blur lg:flex dark:bg-gray-900/75 dark:text-gray-200"
            >
              <span class="h-1.5 w-1.5 rounded-full bg-primary"></span>
              {{ t('muse.intro.hero.chipLang') }}
            </div>

            <!-- Audio bar mockup -->
            <div
              class="absolute inset-x-3 bottom-3 flex items-center gap-3 rounded-2xl bg-white/88 px-3 py-2.5 shadow-xl backdrop-blur sm:inset-x-6 sm:bottom-6 sm:px-4 sm:py-3 dark:bg-gray-900/75"
            >
              <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-white sm:h-9 sm:w-9">
                <Play class="ml-0.5 h-4 w-4" aria-hidden="true" />
              </div>
              <div class="min-w-0 flex-1 text-left">
                <div class="truncate text-[11px] font-medium text-gray-800 sm:text-xs dark:text-gray-100">
                  {{ t('muse.intro.hero.nowPlaying') }}
                </div>
                <div class="mt-1.5 flex h-5 items-end gap-[3px]">
                  <span
                    v-for="(height, index) in waveform"
                    :key="index"
                    class="w-[3px] rounded-full bg-primary/55"
                    :style="{ height: `${height}px` }"
                  ></span>
                </div>
              </div>
              <div class="shrink-0 text-[11px] tabular-nums text-gray-500 dark:text-gray-400">00:12</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <IntroFeatureGrid />

    <IntroShowcase
      id="voice"
      :tag="t('muse.intro.voice.tag')"
      :title="t('muse.intro.voice.title')"
      :desc="t('muse.intro.voice.desc', { name: siteStore.displayName })"
      :bullets="voiceBullets"
      :cta="t('muse.intro.voice.cta')"
      :image="voiceImage"
      image-class="bg-white"
      @action="openVoice"
    >
      <div
        class="absolute inset-x-4 bottom-4 flex items-center gap-3 rounded-2xl border border-gray-100 bg-white/90 px-4 py-3 shadow-lg backdrop-blur sm:inset-x-6 sm:bottom-6 dark:border-gray-800 dark:bg-gray-900/80"
      >
        <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-white">
          <Play class="ml-0.5 h-4 w-4" aria-hidden="true" />
        </div>
        <div class="min-w-0 flex-1 text-left">
          <div class="truncate text-xs font-medium text-gray-800 dark:text-gray-100">
            {{ t('muse.intro.voice.track') }}
          </div>
          <div class="mt-1.5 flex h-4 items-end gap-[3px]">
            <span
              v-for="(height, index) in waveform.slice(0, 18)"
              :key="index"
              class="w-[3px] rounded-full bg-primary/50"
              :style="{ height: `${Math.max(5, height - 4)}px` }"
            ></span>
          </div>
        </div>
        <div class="shrink-0 text-[11px] tabular-nums text-gray-500 dark:text-gray-400">00:07</div>
      </div>
    </IntroShowcase>

    <IntroShowcase
      id="video"
      reverse
      :tag="t('muse.intro.video.tag')"
      :title="t('muse.intro.video.title')"
      :desc="t('muse.intro.video.desc')"
      :bullets="videoBullets"
      :cta="t('muse.intro.video.cta')"
      :image="videoImage"
      image-class="bg-gray-950"
      @action="openVideo"
    >
      <div
        class="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-gray-950/55 px-3 py-1.5 text-[11px] font-medium text-white backdrop-blur sm:left-6 sm:top-6"
      >
        <span class="h-1.5 w-1.5 rounded-full bg-red-400"></span>
        REC · 4K
      </div>
    </IntroShowcase>

    <!-- Capability stats -->
    <section class="relative overflow-hidden py-12 sm:py-16">
      <div class="mx-auto max-w-6xl px-5 sm:px-8">
        <div
          class="grid grid-cols-3 divide-x divide-gray-200/70 rounded-[24px] border border-gray-100 bg-panel/50 px-2 py-7 text-center sm:px-10 sm:py-12 dark:divide-gray-800 dark:border-gray-800 dark:bg-panel/30"
        >
          <div v-for="stat in stats" :key="stat.label" class="min-w-0 px-1.5 sm:px-0">
            <div class="text-2xl font-black tabular-nums tracking-tight text-primary sm:text-5xl">{{ stat.value }}</div>
            <div class="mt-1.5 text-[11px] leading-tight text-gray-500 sm:mt-2 sm:text-sm dark:text-gray-400">
              {{ stat.label }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <IntroWays />

    <!-- Final CTA -->
    <section class="px-5 pb-16 pt-4 sm:px-8 sm:pb-24">
      <div class="relative mx-auto max-w-6xl overflow-hidden rounded-[28px] border border-primary-100/70 bg-gradient-to-br from-primary-50 via-surface to-accent/5 px-6 py-12 text-center sm:px-12 sm:py-16 dark:border-primary-800/50 dark:from-primary-900/20 dark:via-surface dark:to-accent/10">
        <div
          class="pointer-events-none absolute inset-0 bg-[radial-gradient(circle,rgba(148,163,184,0.22)_1px,transparent_1px)] [background-size:22px_22px] [mask-image:radial-gradient(ellipse_at_center,white,transparent_75%)]"
        ></div>
        <div class="relative">
          <div class="text-2xl font-black tracking-tight sm:text-4xl">{{ t('muse.intro.final.title', { name: siteStore.displayName }) }}</div>
          <div class="mx-auto mt-3 max-w-xl text-sm text-gray-500 sm:text-base dark:text-gray-400">
            {{ t('muse.intro.final.desc') }}
          </div>
          <a-button type="primary" size="large" class="!mt-7 !h-12 !rounded-full !px-8" @click="primaryAction">
            <div class="inline-flex items-center gap-1.5">
              {{ isAuthed ? t('muse.intro.hero.ctaApp') : t('muse.intro.final.button') }}
              <ArrowRight class="h-4 w-4" aria-hidden="true" />
            </div>
          </a-button>
          <div class="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-gray-500 dark:text-gray-400">
            <div class="inline-flex items-center gap-1.5">
              <Check class="h-4 w-4 text-primary" aria-hidden="true" />
              {{ t('muse.intro.hero.noteNoEdit') }}
            </div>
            <div class="inline-flex items-center gap-1.5">
              <Check class="h-4 w-4 text-primary" aria-hidden="true" />
              {{ t('muse.intro.hero.noteMultilingual') }}
            </div>
            <div class="inline-flex items-center gap-1.5">
              <Check class="h-4 w-4 text-primary" aria-hidden="true" />
              {{ t('muse.intro.hero.noteApi') }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <IntroFooter />
  </div>
</template>
